import { useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { ShieldCheck, KeyRound, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public recovery page — operator pastes the WEBHOOK_CRON_SECRET from the
 * Emergent deployment panel and sets a brand-new admin password. On success
 * they are taken to /login. No auth required; the endpoint itself is
 * secret-protected and rate-limited server-side.
 */
export default function AdminRecoveryPage() {
  const [secret, setSecret] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!secret.trim()) return toast.error("Secret boş olamaz");
    if (pw.length < 8) return toast.error("Yeni şifre en az 8 karakter olmalı");
    if (pw !== pw2) return toast.error("Şifreler eşleşmiyor");
    setBusy(true);
    try {
      await axios.post(`${API}/auth/admin-reset`, { secret: secret.trim(), new_password: pw });
      toast.success("Admin şifresi güncellendi. Yeni şifreyle giriş yapabilirsiniz.");
      setTimeout(() => { window.location.href = "/login"; }, 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "İşlem başarısız — secret veya şifreyi kontrol edin.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 grid place-items-center p-6" data-testid="admin-recovery-page">
      <Toaster position="top-center" richColors />
      <Card className="w-full max-w-md border-slate-200 shadow-xl">
        <CardContent className="p-6 sm:p-8 space-y-4">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-[#D97706] text-white grid place-items-center">
              <ShieldCheck size={22} />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1B4D3E]">Kurtarma</div>
              <h1 className="text-xl font-extrabold tracking-tight text-slate-900">Admin Şifre Sıfırla</h1>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 space-y-1.5">
            <div className="font-bold">Nasıl kullanılır?</div>
            <ol className="list-decimal list-inside space-y-0.5">
              <li>Emergent panelinde <b>Deployments → Environment Variables</b> açın</li>
              <li><code className="bg-amber-100 px-1 rounded">WEBHOOK_CRON_SECRET</code> satırının <b>Reveal</b> butonuna basıp değeri kopyalayın</li>
              <li>Aşağıya yapıştırın ve yeni şifrenizi girin</li>
            </ol>
          </div>

          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Kurtarma Secret'ı</Label>
              <div className="relative">
                <Input
                  type={showSecret ? "text" : "password"}
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  placeholder="WEBHOOK_CRON_SECRET değerini yapıştırın"
                  data-testid="recovery-secret"
                  autoComplete="off"
                  required
                  className="pr-10 font-mono text-xs"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                  tabIndex={-1}
                >
                  {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <Label>Yeni Admin Şifresi (min 8 karakter)</Label>
              <Input
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                data-testid="recovery-new-pw"
                autoComplete="new-password"
                minLength={8}
                required
              />
            </div>
            <div>
              <Label>Yeni Şifre (Tekrar)</Label>
              <Input
                type="password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                data-testid="recovery-new-pw2"
                autoComplete="new-password"
                required
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-[#D97706] hover:bg-[#B45309] text-white gap-2 h-11"
              disabled={busy}
              data-testid="recovery-submit"
            >
              <KeyRound size={16} /> {busy ? "İşleniyor…" : "Şifreyi Sıfırla"}
            </Button>
          </form>

          <div className="text-[11px] text-slate-400 text-center pt-2 border-t border-slate-100">
            Bu sayfa yalnızca kurtarma amaçlıdır. İşlem başarılı olduktan sonra Emergent panelinden <b>WEBHOOK_CRON_SECRET</b> değerini yenilemenizi tavsiye ederiz.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
